import { normalizeName } from '../shared/naming';
import type { Activity } from '../structure/activity';
import { activityNaturalKey } from '../structure/activity';
import type { Discipline } from '../structure/discipline';
import type { Floor } from '../structure/floor';
import { floorNaturalKey } from '../structure/floor';
import type { Tower } from '../structure/tower';
import { parseImportDate, parseImportQuantity } from './import-parsing';
import { materialNaturalKey, type Material } from './material';
import { resolveUnit } from './material-import';
import type { ScheduleEntry } from './schedule-entry';
import { roundQuantity, type UnitOfMeasure } from './unit-of-measure';

/**
 * Importação do cronograma.
 *
 * Uma planilha vira linhas de cronograma através de uma função **pura**: recebe as linhas
 * já lidas do arquivo e o retrato da obra, devolve o que aconteceria com cada uma. Nada
 * aqui grava, e é por isso que a pré-visualização pode ser exibida sem tocar no domínio
 * (RN-030).
 *
 * A planilha do cliente descreve a obra em texto — "Torre A", "3º Pavimento", "Alvenaria",
 * "Tijolo 19x30". Cada um desses nomes precisa virar uma entidade existente, e o que
 * decide o destino da linha é justamente se isso foi possível.
 */

export type ScheduleImportField =
  | 'tower'
  | 'floor'
  | 'floorLevel'
  | 'discipline'
  | 'materialCode'
  | 'materialName'
  | 'quantity'
  | 'unit'
  | 'requiredDate'
  | 'plannedStart'
  | 'plannedEnd'
  | 'notes';

export interface ScheduleImportColumn {
  readonly field: ScheduleImportField;
  readonly label: string;
  readonly required: boolean;
  readonly synonyms: readonly string[];
}

export const SCHEDULE_IMPORT_COLUMNS: readonly ScheduleImportColumn[] = [
  {
    field: 'tower',
    label: 'Torre',
    required: true,
    synonyms: ['torre', 'bloco', 'edificio', 'predio'],
  },
  {
    field: 'floor',
    label: 'Pavimento',
    required: true,
    synonyms: ['pavimento', 'andar', 'piso', 'nivel', 'pav'],
  },
  {
    field: 'floorLevel',
    label: 'Nível do pavimento',
    required: false,
    synonyms: ['nivel do pavimento', 'cota', 'ordem do pavimento', 'numero do pavimento'],
  },
  {
    field: 'discipline',
    label: 'Disciplina',
    required: true,
    synonyms: ['disciplina', 'servico', 'etapa', 'frente', 'atividade', 'especialidade'],
  },
  {
    field: 'materialCode',
    label: 'Código do material',
    required: false,
    synonyms: [
      'codigo',
      'cod',
      'codigo do material',
      'codigo do item',
      'sku',
      'referencia',
      'item',
    ],
  },
  {
    field: 'materialName',
    label: 'Material',
    required: true,
    synonyms: ['material', 'descricao', 'insumo', 'produto', 'descricao do material'],
  },
  {
    field: 'quantity',
    label: 'Quantidade',
    required: true,
    synonyms: ['quantidade', 'qtd', 'qtde', 'quant', 'volume'],
  },
  {
    field: 'unit',
    label: 'Unidade',
    required: false,
    synonyms: ['unidade', 'un', 'und', 'unid', 'um'],
  },
  {
    field: 'requiredDate',
    label: 'Necessário em',
    required: true,
    synonyms: [
      'necessario em',
      'data necessaria',
      'data de necessidade',
      'necessidade',
      'entrega',
      'data de entrega',
      'previsao de entrega',
    ],
  },
  {
    field: 'plannedStart',
    label: 'Início previsto',
    required: false,
    synonyms: ['inicio previsto', 'inicio', 'data inicio', 'comeco', 'previsao de inicio'],
  },
  {
    field: 'plannedEnd',
    label: 'Término previsto',
    required: false,
    synonyms: ['termino previsto', 'termino', 'fim', 'data fim', 'conclusao'],
  },
  {
    field: 'notes',
    label: 'Observação',
    required: false,
    synonyms: ['observacao', 'obs', 'comentario', 'nota', 'detalhe'],
  },
];

function headerKey(value: string): string {
  return normalizeName(value)
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

export type ScheduleColumnMapping = Partial<Record<ScheduleImportField, string>>;

/** Sugestão a partir dos cabeçalhos. Uma coluna serve a um campo só. */
export function suggestScheduleMapping(headers: readonly string[]): ScheduleColumnMapping {
  const mapping: ScheduleColumnMapping = {};
  const taken = new Set<string>();

  for (const column of SCHEDULE_IMPORT_COLUMNS) {
    const match = headers.find((header) => {
      if (taken.has(header)) return false;
      const key = headerKey(header);
      return key === headerKey(column.label) || column.synonyms.includes(key);
    });
    if (match !== undefined) {
      mapping[column.field] = match;
      taken.add(match);
    }
  }

  return mapping;
}

/**
 * Nível do pavimento a partir do nome.
 *
 * Só é usado quando a planilha não traz a coluda de nível e o pavimento precisa ser
 * criado. Erra para 0 em nome que não reconhece — e o pavimento criado com nível errado é
 * corrigível na tela da estrutura, enquanto recusar a linha inteira por causa disso
 * travaria a importação de uma obra inteira.
 */
export function inferFloorLevel(name: string): number {
  const normalized = normalizeName(name);

  if (normalized.includes('terreo') || normalized.includes('térreo')) return 0;

  const subsolo = /(\d+)[º°]?\s*subsolo|subsolo\s*(\d+)/.exec(normalized);
  if (subsolo !== null) return -Number(subsolo[1] ?? subsolo[2] ?? 1);
  if (normalized.includes('subsolo')) return -1;

  const numbered = /(-?\d+)/.exec(normalized);
  if (numbered !== null) return Number(numbered[1]);

  if (normalized.includes('cobertura')) return 999;
  return 0;
}

export type ScheduleRowStatus = 'VALID' | 'WARNING' | 'INVALID' | 'DUPLICATE';

export interface RowIssue {
  readonly field: ScheduleImportField | 'row';
  readonly code: string;
  readonly message: string;
  readonly severity: 'ERROR' | 'WARNING';
}

/** O que seria criado por causa desta linha — some quando a entidade já existe. */
export interface RowCreations {
  readonly tower: boolean;
  readonly floor: boolean;
  readonly activity: boolean;
  readonly material: boolean;
}

export interface ScheduleImportRowPlan {
  readonly rowNumber: number;
  readonly status: ScheduleRowStatus;
  readonly issues: readonly RowIssue[];
  /** Texto original, para a tela mostrar o que veio do arquivo. */
  readonly raw: {
    readonly tower: string;
    readonly floor: string;
    readonly discipline: string;
    /** O que a tela mostra: nome quando existe, código quando não. */
    readonly material: string;
    readonly materialCode: string;
    readonly materialName: string;
    readonly quantity: string;
    readonly unit: string;
    readonly requiredDate: string;
  };
  readonly quantity: number | undefined;
  readonly requiredDate: string | undefined;
  readonly plannedStart: string | undefined;
  readonly plannedEnd: string | undefined;
  readonly notes: string | undefined;
  readonly floorLevel: number;
  readonly resolved: {
    readonly towerId: string | undefined;
    readonly floorId: string | undefined;
    readonly disciplineId: string | undefined;
    readonly activityId: string | undefined;
    readonly materialId: string | undefined;
    readonly unitId: string | undefined;
  };
  readonly creations: RowCreations;
  /** Linha de cronograma existente que seria **atualizada** em vez de criada (RN-032). */
  readonly targetEntryId: string | undefined;
}

export interface ScheduleImportContext {
  readonly towers: readonly Tower[];
  readonly floors: readonly Floor[];
  readonly disciplines: readonly Discipline[];
  readonly activities: readonly Activity[];
  readonly materials: readonly Material[];
  readonly units: readonly UnitOfMeasure[];
  /** Itens do cronograma ativo — é contra eles que a duplicidade é medida (RN-032). */
  readonly entries: readonly ScheduleEntry[];
}

export interface ScheduleImportOptions {
  /** Criar torre, pavimento e atividade que a planilha cita e a obra não tem (RN-035). */
  readonly createMissingStructure: boolean;
  /** Criar material que a planilha cita e o catálogo não tem (RN-010). */
  readonly createMissingMaterials: boolean;
  /** Unidade usada quando a linha não traz uma reconhecível. */
  readonly fallbackUnitId?: string | undefined;
}

export interface ScheduleImportPlan {
  readonly rows: readonly ScheduleImportRowPlan[];
  readonly summary: {
    readonly total: number;
    readonly valid: number;
    readonly warning: number;
    readonly invalid: number;
    readonly duplicate: number;
    /** Linhas que entrariam: válidas + com aviso. */
    readonly applicable: number;
    readonly createEntries: number;
    readonly updateEntries: number;
    readonly newTowers: number;
    readonly newFloors: number;
    readonly newActivities: number;
    readonly newMaterials: number;
  };
}

interface Pending {
  readonly towers: Set<string>;
  readonly floors: Set<string>;
  readonly activities: Set<string>;
  readonly materials: Set<string>;
}

/**
 * Classifica cada linha da planilha.
 *
 * A ordem das verificações não é arbitrária: primeiro o que a linha **diz** (campos
 * obrigatórios, número, data), depois o que ela **encontra** (estrutura, material,
 * unidade), por último o que ela **colide** (duplicata no arquivo, linha já existente).
 * Assim o motivo apresentado é sempre o primeiro problema real, e não um efeito dele.
 *
 * Uma linha inválida nunca contamina as outras: a planilha do mundo real vem com sujeira,
 * e recusar as 500 linhas boas por causa de 3 ruins não ajuda ninguém (RN-034).
 */
export function classifyScheduleImport(
  rows: readonly Record<string, string>[],
  mapping: ScheduleColumnMapping,
  context: ScheduleImportContext,
  options: ScheduleImportOptions,
): ScheduleImportPlan {
  const towerByKey = new Map(context.towers.map((tower) => [normalizeName(tower.name), tower]));
  const floorByKey = new Map(
    context.floors.map((floor) => [`${floor.towerId}|${normalizeName(floor.name)}`, floor]),
  );
  const disciplineByKey = new Map(
    context.disciplines.map((discipline) => [normalizeName(discipline.name), discipline]),
  );
  const activityByKey = new Map(
    context.activities.map((activity) => [
      activityNaturalKey(activity.floorId, activity.disciplineId),
      activity,
    ]),
  );
  const materialByCode = new Map(
    context.materials.map((material) => [materialNaturalKey(material.code), material]),
  );
  const materialByName = new Map(
    context.materials.map((material) => [normalizeName(material.name), material]),
  );
  // A planilha da Fase 3 não tem serviço: ela só alcança as linhas sem serviço (RN-340). A
  // linha de compra com serviço é de outra importação, e o mesmo material na mesma frente
  // com serviço não é a linha que esta planilha descreve.
  const entryByKey = new Map(
    context.entries
      .filter((entry) => entry.serviceId === undefined)
      .map((entry) => [`${entry.activityId}|${entry.materialId}`, entry]),
  );

  // O que linhas anteriores do mesmo arquivo já reservaram: a segunda linha que cita
  // "Torre C" não conta uma segunda torre a criar.
  const pending: Pending = {
    towers: new Set(),
    floors: new Set(),
    activities: new Set(),
    materials: new Set(),
  };
  const seenInFile = new Set<string>();

  const plans: ScheduleImportRowPlan[] = rows.map((raw, index) => {
    const rowNumber = index + 2; // +1 do cabeçalho, +1 porque humano conta do 1
    const issues: RowIssue[] = [];

    const pick = (field: ScheduleImportField): string => {
      const column = mapping[field];
      return column === undefined ? '' : (raw[column] ?? '').trim();
    };

    const towerName = pick('tower');
    const floorName = pick('floor');
    const disciplineName = pick('discipline');
    const materialCode = pick('materialCode');
    const materialName = pick('materialName');
    const quantityText = pick('quantity');
    const unitText = pick('unit');
    const requiredText = pick('requiredDate');
    const startText = pick('plannedStart');
    const endText = pick('plannedEnd');
    const notes = pick('notes');
    const levelText = pick('floorLevel');

    const error = (field: ScheduleImportField | 'row', code: string, message: string): void => {
      issues.push({ field, code, message, severity: 'ERROR' });
    };
    const warn = (field: ScheduleImportField | 'row', code: string, message: string): void => {
      issues.push({ field, code, message, severity: 'WARNING' });
    };

    // ── 1. O que a linha diz ──────────────────────────────────────────────────
    if (towerName === '') error('tower', 'TOWER_EMPTY', 'Torre não informada.');
    if (floorName === '') error('floor', 'FLOOR_EMPTY', 'Pavimento não informado.');
    if (disciplineName === '') error('discipline', 'DISCIPLINE_EMPTY', 'Disciplina não informada.');
    if (materialName === '' && materialCode === '') {
      error('materialName', 'MATERIAL_EMPTY', 'Material não informado.');
    }

    const quantity = parseImportQuantity(quantityText);
    if (!quantity.ok) {
      // O que decide a mensagem é o que a **célula** tinha, não o que sobrou depois de
      // limpar: quem escreveu "à combinar" precisa ler que aquilo não é número, e não
      // que deixou o campo em branco.
      const wasBlank = quantityText === '';
      error(
        'quantity',
        wasBlank ? 'QUANTITY_EMPTY' : 'QUANTITY_FORMAT',
        wasBlank ? 'Quantidade não informada.' : `Quantidade "${quantityText}" não é um número.`,
      );
    } else if (quantity.value! <= 0) {
      error('quantity', 'QUANTITY_NOT_POSITIVE', 'A quantidade deve ser maior que zero.');
    }

    const required = parseImportDate(requiredText);
    if (!required.ok) {
      const wasBlank = requiredText === '';
      error(
        'requiredDate',
        wasBlank ? 'REQUIRED_DATE_EMPTY' : 'REQUIRED_DATE_FORMAT',
        wasBlank
          ? 'Data de necessidade não informada.'
          : `Data "${requiredText}" não foi reconhecida. Use dd/mm/aaaa.`,
      );
    }

    const start = startText === '' ? undefined : parseImportDate(startText);
    if (start !== undefined && !start.ok) {
      error('plannedStart', 'START_FORMAT', `Data "${startText}" não foi reconhecida.`);
    }
    const end = endText === '' ? undefined : parseImportDate(endText);
    if (end !== undefined && !end.ok) {
      error('plannedEnd', 'END_FORMAT', `Data "${endText}" não foi reconhecida.`);
    }
    if (start?.ok === true && end?.ok === true && end.value! < start.value!) {
      error('plannedEnd', 'PERIOD_INVALID', 'O término previsto é anterior ao início.');
    }

    // ── 2. O que a linha encontra ─────────────────────────────────────────────
    const towerKey = normalizeName(towerName);
    const tower = towerByKey.get(towerKey);
    const towerMissing = towerName !== '' && tower === undefined;

    const floorKeyExisting = tower === undefined ? undefined : floorNaturalKey(tower.id, floorName);
    const floor = floorKeyExisting === undefined ? undefined : floorByKey.get(floorKeyExisting);
    const floorMissing = floorName !== '' && floor === undefined;

    const discipline = disciplineByKey.get(normalizeName(disciplineName));
    if (disciplineName !== '' && discipline === undefined) {
      // Disciplina é catálogo curado (RN-007): criar uma por planilha encheria o sistema
      // de "Alvenria" e "Alvenaria " como coisas diferentes.
      error(
        'discipline',
        'DISCIPLINE_NOT_FOUND',
        `Disciplina "${disciplineName}" não existe. Cadastre-a antes de importar.`,
      );
    }

    if ((towerMissing || floorMissing) && !options.createMissingStructure) {
      // RN-035 — estrutura inexistente é inválida por padrão.
      error(
        towerMissing ? 'tower' : 'floor',
        'STRUCTURE_NOT_FOUND',
        towerMissing
          ? `Torre "${towerName}" não existe nesta obra. Marque a opção de criar a estrutura faltante.`
          : `Pavimento "${floorName}" não existe em "${towerName}". Marque a opção de criar a estrutura faltante.`,
      );
    }

    const material =
      materialCode === ''
        ? materialByName.get(normalizeName(materialName))
        : materialByCode.get(materialNaturalKey(materialCode));

    const materialMissing = (materialCode !== '' || materialName !== '') && material === undefined;
    if (materialMissing && !options.createMissingMaterials) {
      // RN-010 — material é entidade, não texto livre.
      error(
        'materialName',
        'MATERIAL_NOT_FOUND',
        `Material "${materialCode === '' ? materialName : materialCode}" não está no catálogo. Marque a opção de criar os materiais novos.`,
      );
    }
    if (materialMissing && options.createMissingMaterials && materialCode === '') {
      error(
        'materialCode',
        'MATERIAL_CODE_REQUIRED',
        'Material novo precisa de código: é ele que liga o item ao cadastro de compras.',
      );
    }

    const resolvedUnit = resolveUnit(unitText, context.units);
    const unitId = resolvedUnit?.id ?? material?.unitId ?? options.fallbackUnitId;
    if (unitId === undefined) {
      error(
        'unit',
        'UNIT_NOT_FOUND',
        unitText === ''
          ? 'Unidade não informada e o material não tem unidade padrão.'
          : `Unidade "${unitText}" não existe no catálogo.`,
      );
    }

    // RN-012 — divergência de unidade é aviso, não erro.
    if (
      resolvedUnit !== undefined &&
      material !== undefined &&
      resolvedUnit.id !== material.unitId
    ) {
      const standard = context.units.find((unit) => unit.id === material.unitId);
      warn(
        'unit',
        'UNIT_DIVERGENT',
        `Lançado em ${resolvedUnit.name}; a unidade padrão do material é ${standard?.name ?? '?'}. Nada é convertido.`,
      );
    }

    // RN-023 — material previsto para depois do início da frente.
    if (required.ok && start?.ok === true && required.value! > start.value!) {
      warn(
        'requiredDate',
        'MATERIAL_AFTER_START',
        'O material chega depois do início previsto da atividade.',
      );
    }

    // ── 3. O que a linha colide ───────────────────────────────────────────────
    const activityKey =
      floor === undefined || discipline === undefined
        ? `${towerKey}|${normalizeName(floorName)}|${normalizeName(disciplineName)}`
        : activityNaturalKey(floor.id, discipline.id);

    const activity =
      floor === undefined || discipline === undefined ? undefined : activityByKey.get(activityKey);

    const materialIdentity =
      material?.id ??
      (materialCode === ''
        ? `novo|${normalizeName(materialName)}`
        : `novo|${materialNaturalKey(materialCode)}`);

    const fileKey = `${activityKey}|${materialIdentity}`;
    const isDuplicateInFile = seenInFile.has(fileKey);
    if (!isDuplicateInFile) seenInFile.add(fileKey);

    const existingEntry =
      activity === undefined || material === undefined
        ? undefined
        : entryByKey.get(`${activity.id}|${material.id}`);

    // ── 4. Veredito ───────────────────────────────────────────────────────────
    const hasError = issues.some((issue) => issue.severity === 'ERROR');
    let status: ScheduleRowStatus;

    if (hasError) {
      status = 'INVALID';
    } else if (isDuplicateInFile) {
      // RN-037 — duplicidade interna do arquivo: a primeira ocorrência vale.
      status = 'DUPLICATE';
      issues.push({
        field: 'row',
        code: 'DUPLICATE_IN_FILE',
        message: 'Esta combinação de pavimento, disciplina e material já apareceu no arquivo.',
        severity: 'ERROR',
      });
    } else if (issues.length > 0) {
      status = 'WARNING';
    } else {
      status = 'VALID';
    }

    // Só linha que vai entrar reserva criação: uma linha inválida não pode "gastar" a
    // torre que ela cita, senão a linha boa seguinte deixaria de criá-la.
    const willApply = status === 'VALID' || status === 'WARNING';

    /** Marca a criação uma única vez por entidade, ao longo de todo o arquivo. */
    const reserve = (set: Set<string>, key: string, missing: boolean): boolean => {
      if (!willApply || !missing || set.has(key)) return false;
      set.add(key);
      return true;
    };

    const creations: RowCreations = {
      tower: reserve(pending.towers, towerKey, towerMissing),
      floor: reserve(pending.floors, `${towerKey}|${normalizeName(floorName)}`, floorMissing),
      activity: reserve(pending.activities, activityKey, activity === undefined),
      material: reserve(pending.materials, materialIdentity, materialMissing),
    };

    const decimals = resolvedUnit?.decimals ?? 2;

    return {
      rowNumber,
      status,
      issues,
      raw: {
        tower: towerName,
        floor: floorName,
        discipline: disciplineName,
        material: materialName === '' ? materialCode : materialName,
        materialCode,
        materialName,
        quantity: quantityText,
        unit: unitText,
        requiredDate: requiredText,
      },
      quantity: quantity.ok ? roundQuantity(quantity.value!, decimals) : undefined,
      requiredDate: required.ok ? required.value : undefined,
      plannedStart: start?.ok === true ? start.value : undefined,
      plannedEnd: end?.ok === true ? end.value : undefined,
      notes: notes === '' ? undefined : notes,
      floorLevel: levelText === '' ? inferFloorLevel(floorName) : Number(levelText),
      resolved: {
        towerId: tower?.id,
        floorId: floor?.id,
        disciplineId: discipline?.id,
        activityId: activity?.id,
        materialId: material?.id,
        unitId,
      },
      creations,
      targetEntryId: existingEntry?.id,
    };
  });

  const count = (predicate: (row: ScheduleImportRowPlan) => boolean): number =>
    plans.filter(predicate).length;

  const applicableRows = plans.filter((row) => row.status === 'VALID' || row.status === 'WARNING');

  return {
    rows: plans,
    summary: {
      total: plans.length,
      valid: count((row) => row.status === 'VALID'),
      warning: count((row) => row.status === 'WARNING'),
      invalid: count((row) => row.status === 'INVALID'),
      duplicate: count((row) => row.status === 'DUPLICATE'),
      applicable: applicableRows.length,
      createEntries: applicableRows.filter((row) => row.targetEntryId === undefined).length,
      updateEntries: applicableRows.filter((row) => row.targetEntryId !== undefined).length,
      newTowers: applicableRows.filter((row) => row.creations.tower).length,
      newFloors: applicableRows.filter((row) => row.creations.floor).length,
      newActivities: applicableRows.filter((row) => row.creations.activity).length,
      newMaterials: applicableRows.filter((row) => row.creations.material).length,
    },
  };
}

export const SCHEDULE_ROW_STATUS_LABEL: Readonly<Record<ScheduleRowStatus, string>> = {
  VALID: 'Válida',
  WARNING: 'Com aviso',
  INVALID: 'Com erro',
  DUPLICATE: 'Repetida',
};
